import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus'
import type { ConfigurableVariant } from '@pylot-data/pylotschema'
import { VariantSelectorProps } from '../VariantSelector/VariantSelector'

export type DisabledValues = Record<string, string[]>

/**
 * Function that based on selected options for each option generates disabled
 * option values - values that will lead to OOS (out of stock) products once selected.
 * @param options configurable product options
 * @param selectedOptions selected options
 * @param variants configurable product variants
 * @returns object of disabled values for every option
 */
export const getDisabledOptionValues = (
    options: VariantSelectorProps['options'],
    selectedOptions: VariantSelectorProps['selectedOptions'],
    variants: ConfigurableVariant[]
): DisabledValues | null => {
    // if no options at all - skip calculations
    if (!Object.keys(selectedOptions)) return null

    // Get all options with option values as disabled by default.
    // template: {[optionCode]: {[optionValue]: false}}
    // example: {color: {154: false, 155: false}}
    const disabledOptions = options.reduce((acc, { code, values }) => {
        acc[code] = values!.map((val) => val.label!)
        return acc
    }, {} as DisabledValues)

    variants.forEach(({ product, attributes }) => {
        const inStock = product?.stock_status === ProductStockStatus.InStock

        if (inStock) {
            // get all variant options
            // example: {color: '154', size: 'XL'}
            const variantOptions: Record<string, string> = Object.fromEntries(
                attributes!.map((attr) => [attr!.code, attr!.label])
            )

            // Go through each option to check if it has alternative value for one option (`optionCheckedForAlternatives`)
            // while matching values of other options
            Object.keys(selectedOptions).forEach(
                (optionCheckedForAlternatives) => {
                    // Check if current variant's option values match selected option values
                    // EXCEPT for the current option - `optionCheckedForAlternatives`
                    // Example:
                    // --- 1 ---
                    // * going through `color` option
                    // * Selected options {color: 12, size: `L`}
                    // * Current variant's options {color: 14 size: `L`}
                    // Result: TRUE, as all options other than `color` are equal
                    // --- 2 ---
                    // * going through `color` option
                    // * Selected options {color: 12, size: null}
                    // * Current variant's options {color: 14 size: `L`}
                    // Result: TRUE, as option is not selected so we can match any value to it
                    const isAlternativeValueFound = Object.entries(
                        selectedOptions
                    ).every(
                        ([optCode, selectedValue]) =>
                            // skip if option code is `optionCheckedForAlternatives`
                            optCode === optionCheckedForAlternatives ||
                            // for other option codes, check if value is not selected so it matches ANY
                            !selectedValue ||
                            // or check if it is the same as current variant's option value
                            selectedValue === variantOptions[optCode]
                    )

                    if (isAlternativeValueFound) {
                        // Example:
                        // * going through the `color` option
                        // * other options matched
                        // Meaning:
                        // * Current variant has the same configurable option values as selected options,
                        // but the `color` option
                        // * Current variant is in stock.
                        // Result: `color` swatch with the value of current variant's `color` option should be
                        // marked as enabled, as by clicking on it user will be able to add product to cart.
                        const enabledValue =
                            variantOptions[optionCheckedForAlternatives]
                        const allValues =
                            disabledOptions[optionCheckedForAlternatives] ?? []
                        disabledOptions[optionCheckedForAlternatives] =
                            allValues.filter((val) => val !== enabledValue)
                    }
                }
            )
        }
    })

    return disabledOptions
}
